import React, { useEffect, useState, useMemo } from 'react';
import Axios from 'axios';

const Reports = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    Axios.get(`http://localhost:3001/api/reports/individual/${userId}`)
      .then((res) => {
        setReportData(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching reports:", err);
        setLoading(false);
      });
  }, []);

  // --- CORE FINANCIAL CALCULATIONS ---
  // Hooks must be called before any conditional return
  const totalInvoices = reportData.length;

  const totalDueSum = reportData.reduce((acc, row) => acc + (Number(row.duebal) || 0), 0);
  const totalDiscountSum = reportData.reduce((acc, row) => acc + (Number(row.totalDiscounts) || 0), 0);

  const totalVatSum = reportData.reduce((acc, row) => {
    const totnetrevenue = Number(row.netRevenue) || 0;
    const totaldue = Number(row.duebal) || 0;
    const vatPercent = typeof row.vat === 'string' 
        ? Number(row.vat.replace(/[^0-9.]/g, '')) 
        : (Number(row.vat) || 0);

    const vattcal = totnetrevenue - totaldue;
    return acc + (vattcal * (vatPercent / 100));
  }, 0);

  const totalNetRevenueIncludingVat = reportData.reduce((acc, curr) => acc + Number(curr.netRevenue || 0), 0);
  const netRevenueExcludingVat = (totalNetRevenueIncludingVat - totalVatSum) - totalDueSum;

  const totalFiscalYears = useMemo(() => {
    if (reportData.length === 0) return 0;
    // Assuming row.month is a date-string the browser can parse
    const dates = reportData.map(row => new Date(row.month).getTime());
    const minDate = new Date(Math.min(...dates));
    const maxDate = new Date(Math.max(...dates));

    let years = maxDate.getFullYear() - minDate.getFullYear();
    if (maxDate.getMonth() < minDate.getMonth() || 
       (maxDate.getMonth() === minDate.getMonth() && maxDate.getDate() < minDate.getDate())) {
      years--;
    }
    return Math.max(1, years + 1); 
  }, [reportData]);

  // Loading state handled AFTER all hooks
  if (loading) return <div style={{ color: 'white', padding: '20px' }}>Loading Financial Data...</div>;

return (
  <div style={containerStyle}>
    <h2 style={{ color: '#e9edef', marginBottom: '20px', borderBottom: '2px solid #00a884', paddingBottom: '10px' }}>
      Financial Overview Individual Report
    </h2>

    {/* TOP METRICS CARDS (Stays the same) */}
    <div style={statsGrid}>
      <div style={cardStyle}>
        <span style={labelStyle}>Total Invoices</span>
        <h2 style={valueStyle}>{totalInvoices}</h2>
      </div>
      <div style={cardStyle}>
        <span style={labelStyle}>Total Age of Business</span>
        <h2 style={valueStyle}>{totalFiscalYears} Year(s)</h2>
      </div>
      <div style={cardStyle}>
        <span style={labelStyle}>Total Discount Given</span>
        <h2 style={{ ...valueStyle, color: '#e74c3c' }}>TK {totalDiscountSum.toLocaleString()}/-</h2>
      </div>
      <div style={cardStyle}>
        <span style={labelStyle}>Total VAT Collected</span>
        <h2 style={{ ...valueStyle, color: '#ffd000' }}>TK {totalVatSum.toLocaleString()}/-</h2>
      </div>
      <div style={cardStyle}>
          <span style={labelStyle}>Total Due Balance</span>
          <h2 style={{ ...valueStyle, color: '#ff1900' }}>
            TK {totalDueSum.toLocaleString()}/-
          </h2>
        </div>
      <div style={cardStyle}>
        <span style={labelStyle}>Total Net Revenue (Excl. VAT)</span>
        <h2 style={{ ...valueStyle, color: '#00a884' }}>TK {netRevenueExcludingVat.toLocaleString()}/-</h2>
      </div>
    </div>

    {/* TABLE (UI preserved, mapping individual rows) */}
    <div style={tableWrapper}>
      <h3 style={{ padding: '15px', margin: 0, backgroundColor: '#011433' }}>Individual Invoice Breakdown</h3>
      <table style={tableStyle}>
        <thead>
          <tr style={headerRowStyle}>
        <th>SL No.</th>
        <th>Date</th>
        <th>Invoice No.</th>
        <th>Gross Revenue</th>
        <th>Discount<br/>Amount</th>
        <th>Taxable Amount<br/>(Incl. Discounts)</th> {/* Added header to match the new column */}
        <th>Value Added Tax<br/>(VAT)</th>
        <th>Revenue<br/>(Incl. VAT)</th>
        <th>Amount Received<br/>From Customer</th>
        <th>Due Balance<br/>(Current Due)</th>
        <th>Net Revenue<br/>(Excl. VAT)</th>
          </tr>
        </thead>
        <tbody>
          {reportData.map((row, index) => {
            const gross = Number(row.grossRevenue) || 0;
            const discount = Number(row.totalDiscounts) || 0;
            const totdue = Number(row.duebal) || 0;
            const totnetrev = Number(row.netRevenue) || 0;
            const taxableAmount = (gross - discount); 
            const vatPercent = typeof row.vat === 'string' ? Number(row.vat.replace(/[^0-9.]/g, '')) : (Number(row.vat) || 0);
            const tRevenuevat = (totnetrev - totdue) * (vatPercent / 100);
            const NetProfitAmount = (totnetrev - totdue) - tRevenuevat;
            const NetReceivedAmount = totnetrev - totdue;  

            return (
              <tr key={index} style={rowStyle}>
                <td>{index + 1}</td> {/* New Serial Column */}
                <td>{` ${new Date(row.month).getDate()}${ ["th", "st", "nd", "rd"][(new Date(row.month).getDate() % 100 - 20) % 10] || ["th", "st", "nd", "rd"][new Date(row.month).getDate()] || "th" } ${new Date(row.month).toLocaleDateString('en-US', { month: 'short' })} ${new Date(row.month).getFullYear()}`}</td>
                <td>{row.inno}</td>
                <td>TK {gross.toLocaleString()}</td>
                <td style={{ color: '#e74c3c' }}>- TK {discount.toLocaleString()}</td>
                <td>TK {taxableAmount.toLocaleString()}</td> 
                <td style={{ color: '#ffd000' }}>+ TK {tRevenuevat.toLocaleString()}</td>
                <td style={{ color: '#fff' }}>TK {totnetrev.toLocaleString()}</td>
                <td style={{ fontWeight: 'bold', color: '#00a7fa' }}>TK {NetReceivedAmount.toLocaleString()}</td>
                <td style={{ fontWeight: 'bold',  color: totdue === 0 ? '#ffd001' : '#ff3300' }}>{totdue === 0 ? "..." : `TK ${totdue.toLocaleString()}`}</td> 
                <td style={{ fontWeight: 'bold', color: '#00a884' }}>TK {NetProfitAmount.toLocaleString()}</td> 
              </tr>
            );
          })}
        </tbody>
      </table>
</div>

    </div>
  );
};

// --- STYLES (Dark Mode Aesthetic) ---
const containerStyle = { padding: '10px', maxWidth: '1920px', margin: '0 auto' };
const statsGrid = { display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '15px', marginBottom: '30px' }; 
const cardStyle = { backgroundColor: '#011433', padding: '15px', borderRadius: '12px', textAlign: 'center', boxShadow: '0 4px 6px rgba(0,0,0,0.3)' };
const labelStyle = { color: '#ffffff', fontSize: '12px', textTransform: 'uppercase' }; 
const valueStyle = { fontSize: '20px', margin: '10px 0 0 0', color: '#ffffff' };
const tableWrapper = { backgroundColor: '#011433', borderRadius: '12px', overflow: 'hidden', color: '#e9edef' };
const tableStyle = { width: '100%', borderCollapse: 'collapse' };
const headerRowStyle = { backgroundColor: '#000b1d', textAlign: 'left', color: '#8696a0' };
const rowStyle = { borderBottom: '1px solid #202c33', padding: '15px' };

export default Reports;